import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { COMPANY, MEDIA } from "../data/site";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { ShieldCheck, Hammer, Globe, Award } from "lucide-react";
import { usePageTitle } from "../lib/usePageTitle";
import { useLocalePath } from "../lib/useLocalePath";

const PILLARS = [
    { key: "p1", icon: Hammer },
    { key: "p2", icon: ShieldCheck },
    { key: "p3", icon: Globe },
    { key: "p4", icon: Award },
];

export default function About() {
    usePageTitle("meta.about");
    const { t } = useTranslation();
    const lp = useLocalePath();

    return (
        <main data-testid="page-about" className="pt-32">
            <section className="px-6 md:px-12 lg:px-20 pt-12 pb-20">
                <Reveal>
                    <p className="text-[10px] uppercase tracking-[0.35em] text-[#C5A059]">
                        {t("about.overline", { year: COMPANY.foundedYear })}
                    </p>
                    <h1 className="mt-6 font-heading text-5xl md:text-6xl lg:text-7xl text-[#E8E3D9] leading-[1.02] tracking-tighter max-w-5xl">
                        {t("about.h1_a")}{" "}
                        <span className="italic text-[#C5A059]">
                            {t("about.h1_b")}
                        </span>{" "}
                        {t("about.h1_c")}
                    </h1>
                </Reveal>
            </section>

            <section className="px-6 md:px-12 lg:px-20 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
                <Reveal className="lg:col-span-7">
                    <p className="text-[#E8E3D9] text-lg md:text-xl font-light leading-relaxed">
                        {t("about.para1")}
                    </p>
                    <p className="dropcap mt-8 text-[#E8E3D9] text-lg md:text-xl font-light leading-relaxed">
                        <Trans
                            i18nKey="about.para2_html"
                            components={{
                                b: (
                                    <strong className="text-[#C5A059] font-medium" />
                                ),
                            }}
                        />
                    </p>
                    <p className="mt-8 text-[#A39E93] leading-relaxed font-light">
                        {t("about.para3")}
                    </p>
                </Reveal>

                <Reveal className="lg:col-span-5" delay={120}>
                    <div className="bg-[#1A1A1A] border border-[#2C2C2C] p-8">
                        <p className="text-[10px] uppercase tracking-[0.35em] text-[#C5A059]">
                            {t("about.sidebar.overline")}
                        </p>
                        <dl className="mt-6 space-y-5 text-sm">
                            <div className="flex justify-between gap-4 border-b border-[#2C2C2C] pb-3">
                                <dt className="text-[#A39E93]">
                                    {t("about.sidebar.legal_entity")}
                                </dt>
                                <dd className="text-[#E8E3D9] text-right">
                                    {COMPANY.legalName}
                                </dd>
                            </div>
                            <div className="flex justify-between gap-4 border-b border-[#2C2C2C] pb-3">
                                <dt className="text-[#A39E93]">
                                    {t("about.sidebar.commodities")}
                                </dt>
                                <dd className="text-[#E8E3D9] text-right whitespace-pre-line">
                                    {t("about.sidebar.commodities_value")}
                                </dd>
                            </div>
                            <div className="flex justify-between gap-4 border-b border-[#2C2C2C] pb-3">
                                <dt className="text-[#A39E93]">
                                    {t("about.sidebar.hsn")}
                                </dt>
                                <dd className="text-[#E8E3D9] text-right">
                                    {t("about.sidebar.hsn_value")}
                                </dd>
                            </div>
                            <div className="flex justify-between gap-4 border-b border-[#2C2C2C] pb-3">
                                <dt className="text-[#A39E93]">
                                    {t("about.sidebar.nature")}
                                </dt>
                                <dd className="text-[#E8E3D9] text-right">
                                    {t("about.sidebar.nature_value")}
                                </dd>
                            </div>
                            <div className="flex justify-between gap-4">
                                <dt className="text-[#A39E93]">
                                    {t("about.sidebar.founded")}
                                </dt>
                                <dd className="text-[#E8E3D9]">
                                    {COMPANY.foundedYear}
                                </dd>
                            </div>
                        </dl>
                    </div>
                </Reveal>
            </section>

            {/* PILLARS */}
            <section className="bg-[#1A1A1A] border-y border-[#2C2C2C]">
                <div className="px-6 md:px-12 lg:px-20 py-24 md:py-32">
                    <Reveal>
                        <SectionHeading
                            overline={t("about.pillars.overline")}
                            title={t("about.pillars.title")}
                        />
                    </Reveal>

                    <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#2C2C2C]">
                        {PILLARS.map((p, i) => {
                            const Icon = p.icon;
                            return (
                                <Reveal key={p.key} delay={i * 90}>
                                    <div
                                        data-testid={`about-pillar-${i}`}
                                        className="bg-[#1A1A1A] p-8 lg:p-10 h-full"
                                    >
                                        <Icon
                                            size={28}
                                            className="text-[#C5A059]"
                                            strokeWidth={1.25}
                                        />
                                        <h3 className="mt-8 font-heading text-2xl text-[#E8E3D9] tracking-tight">
                                            {t(`about.pillars.${p.key}.title`)}
                                        </h3>
                                        <p className="mt-4 text-sm text-[#A39E93] font-light leading-relaxed">
                                            {t(`about.pillars.${p.key}.copy`)}
                                        </p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* LOCATION */}
            <section className="px-6 md:px-12 lg:px-20 py-24 md:py-32">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <Reveal className="lg:col-span-6">
                        <img
                            src={MEDIA.heroSecondary}
                            alt={t("gallery.captions.quarry_wall")}
                            className="w-full h-[60vh] object-cover"
                        />
                    </Reveal>
                    <Reveal className="lg:col-span-6 lg:pl-8" delay={120}>
                        <SectionHeading
                            overline={t("about.location.overline")}
                            title={t("about.location.title")}
                            kicker={t("about.location.kicker")}
                        />
                        <Link
                            to={lp("/contact")}
                            data-testid="about-visit-link"
                            className="mt-10 inline-flex items-center gap-3 border border-[#C5A059] text-[#C5A059] px-7 py-3.5 text-[11px] uppercase tracking-[0.25em] hover:bg-[#C5A059] hover:text-[#0D0D0D] transition-colors"
                        >
                            {t("about.location.cta")}
                        </Link>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
